import { DeleteOutlined } from "@ant-design/icons";
import { Button, Tooltip } from "antd";
import { useTranslation } from "react-i18next";

import { STORAGE_KEYS } from "@/constants/StorageKeys";
import { useReferentialLanguages } from "@/entities/referential/referential-languages";
import Create from "@/features/AssetTypes/SeverityGrids/Create";
import Edit from "@/features/AssetTypes/SeverityGrids/Edit";
import { ListProvider } from "@/modules/list";
import ListTable from "@/modules/list-table";
import { useModelDelete } from "@/modules/model-delete";
import { useSidePanel } from "@/modules/side-panel";
import { TABLE_FILTER_TEXT } from "@/modules/table-filter";
import { format, fromNow } from "@/utilities/date";

import { extractTranslatableSeverityGridKeys } from "./SeverityGridHelpers";

const SeverityGridsListTable = ({
  assetTypeId,
  defectTree,
  ...listTableProps
}) => {
  const { t } = useTranslation();
  const { openSidePanel } = useSidePanel();
  const { openDelete } = useModelDelete();
  const { languages } = useReferentialLanguages();

  const buildInitialTreeTranslation = () => {
    const levels = Array.from({ length: 5 });

    const keys = extractTranslatableSeverityGridKeys(defectTree).flatMap(
      (item) => levels.map((_, i) => [`${item}.${i + 1}`, ""])
    );

    return Object.fromEntries(
      languages.map(({ code }) => [code, Object.fromEntries(keys)])
    );
  };

  const onAddSeverityGrid = () => {
    const model = {
      severity_tree_translation: buildInitialTreeTranslation(),
    };

    openSidePanel({
      title: t("severityGrids:create.title"),
      component: <Create model={model} assetTypeId={assetTypeId} />,
      size: "large",
    });
  };

  const onDeleteClick = (severityGrid) => {
    openDelete({
      title: t("severityGrids:delete.label"),
      modelKey: "severitygrid",
      item: severityGrid,
      rootId: assetTypeId,
    });
  };

  const onEditClick = (model) => {
    openSidePanel({
      title: model?.name,
      component: <Edit assetTypeId={assetTypeId} model={model} />,
      size: "large",
    });
  };

  return (
    <ListProvider
      endpoint="severityGrids"
      storageKey={STORAGE_KEYS.SEVERITY_GRID}
      parentId={assetTypeId}
    >
      <ListTable
        columns={[
          {
            title: t("global:lastUpdate"),
            dataIndex: "updated_at",
            sorter: true,
            width: "33%",
            render: (value) => (
              <Tooltip title={format(value, "LLLL")}>{fromNow(value)}</Tooltip>
            ),
          },
          {
            title: t("severityGrids:list.name"),
            dataIndex: "name",
            sorter: true,
            render: (value, grid) => (
              <Button type="link" onClick={() => onEditClick(grid)}>
                {value}
              </Button>
            ),
            ...TABLE_FILTER_TEXT,
          },
          {
            title: "",
            width: 50,
            render: (grid) => (
              <Button type="text" onClick={() => onDeleteClick(grid)} danger>
                <DeleteOutlined />
              </Button>
            ),
          },
        ]}
        addButtonText={t("severityGrids:addSeverityGrid")}
        onAddButton={onAddSeverityGrid}
        {...listTableProps}
      />
    </ListProvider>
  );
};

export default SeverityGridsListTable;
